import { ClerkProvider } from '@clerk/react';
import { type ReactNode, useEffect, useState } from 'react';
import { resolveClerkLocalization } from './clerk-localization';
import i18n from './i18n';

interface ClerkLocalizationProviderProps {
  publishableKey: string;
  clerkJsVersion: string;
  children: ReactNode;
}

// ClerkProvider sits above App, so it cannot read the language from React
// context: track the i18next language in local state and pass the matching
// Clerk bundle. `resolvedLanguage` honours fallbackLng (a browser `fr` resolves
// to `it`), while `language` would stay on the unsupported raw code.
export function ClerkLocalizationProvider({
  publishableKey,
  clerkJsVersion,
  children,
}: ClerkLocalizationProviderProps) {
  const [localization, setLocalization] = useState(() =>
    resolveClerkLocalization(i18n.resolvedLanguage ?? i18n.language),
  );

  useEffect(() => {
    const onLanguageChanged = () =>
      setLocalization(resolveClerkLocalization(i18n.resolvedLanguage ?? i18n.language));
    i18n.on('languageChanged', onLanguageChanged);
    return () => i18n.off('languageChanged', onLanguageChanged);
  }, []);

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      // @ts-expect-error — `__internal_clerkJSVersion` is the only runtime-readable pinning prop in @clerk/react v6 (omitted from ClerkProviderProps types).
      __internal_clerkJSVersion={clerkJsVersion}
      localization={localization}
    >
      {children}
    </ClerkProvider>
  );
}
